import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  addBusinessDays,
  isSameDay,
  isToday,
  isWeekend,
  startOfDay,
} from 'date-fns';
import { AccountPaymentMethod, PaymentDateValidationResult } from '../models';
import { Observable, map, of } from 'rxjs';
import { concatCurrentTimeToDate } from '@arc/unified-payments-frontend/date-utils';

@Injectable({
  providedIn: 'root',
})
export class PaymentDateHelperService {
  constructor(private translationService: TranslateService) {}

  public validatePaymentDate(
    originalPaymentDate: Date,
    selectedPaymentMethodType: AccountPaymentMethod,
    bankHolidayDates: Date[],
    paymentCutoffDateTime: Date,
    createdWithCardOverride?: boolean,
  ): Observable<PaymentDateValidationResult> {
    const result: PaymentDateValidationResult = {
      achPaymentDateWarningVerbiage: null,
      newPaymentDate: createdWithCardOverride
        ? startOfDay(new Date())
        : originalPaymentDate,
      showAchPaymentDateWarning: false,
    };

    if (
      createdWithCardOverride ||
      selectedPaymentMethodType === AccountPaymentMethod.Card ||
      this.isValidAchDate(
        originalPaymentDate,
        bankHolidayDates,
        paymentCutoffDateTime,
      )
    ) {
      return of(result);
    }

    return this.getAchDateWarningVerbiage(
      originalPaymentDate,
      bankHolidayDates,
      paymentCutoffDateTime,
    ).pipe(
      map((warningVerbiage) => {
        result.showAchPaymentDateWarning = true;
        result.newPaymentDate = this.getNextAchDate(originalPaymentDate);
        result.achPaymentDateWarningVerbiage = warningVerbiage;
        return result;
      }),
    );
  }

  public isScheduledPaymentDate(
    paymentDate: Date,
    paymentMethodType: AccountPaymentMethod,
    bankHolidayDates: Date[],
    paymentCutoffDateTime: Date,
    cardOverrideUsed?: boolean,
  ): boolean {
    if (cardOverrideUsed) {
      return false;
    }

    if (!isToday(paymentDate)) {
      return true;
    } else if (paymentMethodType === AccountPaymentMethod.Card) {
      return false;
    }

    return !!(
      this.isPastAchCutoffTime(paymentDate, paymentCutoffDateTime) ||
      isWeekend(paymentDate) ||
      bankHolidayDates?.some((holiday) => isSameDay(paymentDate, holiday))
    );
  }

  private isValidAchDate(
    date: Date,
    bankHolidayDates: Date[],
    cutOffTime: Date,
  ): boolean {
    const dateValue = new Date(date);
    return (
      !isWeekend(dateValue) &&
      !bankHolidayDates?.some((holiday) => isSameDay(dateValue, holiday)) &&
      !this.isPastAchCutoffTime(dateValue, cutOffTime)
    );
  }

  private isPastAchCutoffTime(date: Date, cutOffTime: Date): boolean {
    let isValidCutOff = false;
    const dateValue = concatCurrentTimeToDate(date);
    if (isSameDay(dateValue, cutOffTime)) {
      isValidCutOff = dateValue.getTime() > cutOffTime.getTime();
    }
    return isValidCutOff;
  }

  private getNextAchDate(selectedPaymentDate: Date): Date {
    return startOfDay(addBusinessDays(selectedPaymentDate, 1));
  }

  private getAchDateWarningVerbiage(
    date: Date,
    bankHolidayDates: Date[],
    cutOffTime: Date,
  ): Observable<string> {
    const dateValue = new Date(date);
    if (isWeekend(dateValue)) {
      return of(
        this.translationService.instant(
          'ARC_PAYMENT_DATE_ACH_WEEKEND_PAYMENT_WARNING',
        ) as string,
      );
    } else if (
      bankHolidayDates?.some((holiday) => isSameDay(dateValue, holiday))
    ) {
      return of(
        this.translationService.instant(
          'ARC_PAYMENT_DATE_ACH_HOLIDAY_PAYMENT_WARNING',
        ) as string,
      );
    } else if (this.isPastAchCutoffTime(dateValue, cutOffTime)) {
      return of(
        this.translationService.instant(
          'ARC_PAYMENT_DATE_ACH_AFTER_CUTOFF_WARNING',
        ) as string,
      );
    } else {
      return of(null);
    }
  }
}
